import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent } from "@angular/cdk/stepper";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatStepper } from "@angular/material/stepper";
import { AddressConfirmationComponent } from "@app/address/components/address-confirmation/address-confirmation.component";
import { Address, AddressData, AddressHelper, addressHelper } from "@app/address/model/address";
import { AddressUtilityService, LookupAddressParams } from "@app/address/services/address.utility.service";
import { AddressActions, AddressDispatchers } from "@app/address/store";
import { AddressService } from "@app/address/store/services/address.service";
import { AuditLogInput, createAuditLogInput } from "@app/audit-log/models/audit-log-type.model";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { AuthUserService } from "@app/auth/services/auth-user.service";
import { Customer } from "@app/customer/model/customer";
import { CustomerService } from "@app/customer/services/customer.service";
import { CustomerActions } from "@app/customer/store";
import { Entity } from "@app/entity/model/entity";
import { EntityService } from "@app/entity/services/entity.service";
import { AlulaDevice, ClareSystem, Project, ProjectHelper } from "@app/project/model";
import { accountRoutePaths } from "@app/project/project-routing-constants";
import { ProjectDbService } from "@app/project/services/project-db.service";
import { ProjectService } from "@app/project/services/project.service";
import { ProjectActions } from "@app/project/store";
import { AlulaDispatchers } from "@app/project/store/dispatchers/alula.dispatchers";
import { ProjectDispatchers } from "@app/project/store/dispatchers/project.dispatchers";
import { RouterActions } from "@app/root-store";
import { SnackbarActions } from "@app/root-store/actions";
import { ApiResultState } from "@app/root-store/api-result.state";
import { ClareProcessStep } from "@app/shared/model/utility.model";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { UserManagementService } from "@app/user/service/user-management.service";
import { Action } from "@ngrx/store";
import { BehaviorSubject, Observable, Subject, combineLatest, filter, map, of, switchMap, take, takeUntil, tap } from "rxjs";
import { AddPanelInfoStepComponent } from "./add-panel-info-step/add-panel-info-step.component";
import { CompanyDetailsStepComponent } from "./company-details-step/company-details-step.component";
import { PrimaryCustomerInfoStepComponent } from "./primary-customer-info-step/primary-customer-info-step.component";
import { SystemAddressStepComponent } from "./system-address-step/system-address-step.component";

@Component({
    selector: "app-create-project-wizard",
    templateUrl: "./create-project-wizard.component.html",
    styleUrls: ["./create-project-wizard.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: STEPPER_GLOBAL_OPTIONS,
            useValue: { displayDefaultIndicatorType: false },
        },
    ],
})
export class CreateProjectWizardComponent implements OnDestroy, OnChanges, AfterViewInit, OnInit {
    @Input() initializedData: any;
    @Input() isCompletingProcess = false;
    @Input() uuid = "";
    @Input() existingProject: Project;

    @Output() completeUpdate = new EventEmitter();

    customerAddress$: BehaviorSubject<Address> = new BehaviorSubject(addressHelper.fromDbObject({} as AddressData));
    addressFoundFromGoogle$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    currentProject$: BehaviorSubject<Project> = new BehaviorSubject(ProjectHelper.fromDbObject({}));
    unsubscribe$ = new Subject();
    selectedEntity$: Observable<Entity>;
    createdCustomer: Customer;
    isSubmitAttempted = false;

    customerCreatedEmitter = new EventEmitter();
    @ViewChild("stepper") stepper: MatStepper;
    @ViewChild("companyDetailsStep") companyDetailsStep: CompanyDetailsStepComponent;
    @ViewChild("primaryCustomerInfoStep") primaryCustomerInfoStep: PrimaryCustomerInfoStepComponent;
    @ViewChild("systemAddressStep") systemAddressStep: SystemAddressStepComponent;
    @ViewChild("addPanelInfoStep") addPanelInfoStep: AddPanelInfoStepComponent;

    private readonly _addPanelTag = "add_panel_information";
    private readonly _addCustomerTag = "primary_customer_information";
    public alulaDevice$: Observable<ApiResultState<AlulaDevice>>;

    public steps: ClareProcessStep[] = [
        {
            id: 0,
            tag: "company_detail",
            text: "Company Details",
            isCompleted: false,
        },
        {
            id: 1,
            tag: "system_address",
            text: "System Address",
            isCompleted: false,
        },
        {
            id: 2,
            tag: this._addPanelTag,
            text: "Add Panel Information",
            isCompleted: false,
        },
        {
            id: 3,
            tag: this._addCustomerTag,
            text: "Primary Customer Information",
            isCompleted: false,
        },
    ];

    constructor(
        private readonly projectDispatcher: ProjectDispatchers,
        private readonly cdr: ChangeDetectorRef,
        private readonly auditLogService: AuditLogService,
        private readonly addressService: AddressService,
        private readonly projectService: ProjectService,
        private readonly entityService: EntityService,
        private readonly dialog: MatDialog,
        private readonly customerService: CustomerService,
        private readonly userManagementService: UserManagementService,
        private readonly authUserService: AuthUserService,
        private readonly alulaDispatchers: AlulaDispatchers,
        private readonly addressDispatchers: AddressDispatchers,
        private readonly addressUtilityService: AddressUtilityService,
        private readonly formBuilder: FormBuilder,
        private readonly projectDbService: ProjectDbService
    ) {}
    ngOnInit(): void {
        this.alulaDevice$ = this.projectService.selectAlulaDevice$().pipe(takeUntil(this.unsubscribe$));
    }

    ngAfterViewInit(): void {
        if (this.isCompletingProcess) {
            this.moveToInProgressStep();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.initializedData.currentValue) {
            this.customerAddress$.next(changes.initializedData.currentValue.address || {});
            this.currentProject$.next(changes.initializedData.currentValue.project || {});
            this.createdCustomer = changes.initializedData.currentValue.user;
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    public cancel(): void {
        this.projectDispatcher.dispatch(RouterActions.routerGo({ path: ["/accounts"] }));
    }

    public onTabSelectionChange(tabChange: StepperSelectionEvent) {
        const currentStepIndex = tabChange.selectedIndex;
        this.steps.forEach(step => {
            step.isActive = step.id === currentStepIndex;
            if (step.id === currentStepIndex) {
                step.isTouched = true;
            }
        });
    }

    get isAddressComplete() {
        return AddressHelper.isValid(this.customerAddress$.value);
    }

    stripPhoneFormat(phone: string) {
        return (phone || "").replace(/[()-. ]/g, "");
    }

    displayAddressConfirmation() {
        if (this.addressFoundFromGoogle$.value) {
            this.saveProject();
        } else {
            this.lookupAddress()
                .pipe(take(1))
                .subscribe(address => {
                    if (address) {
                        this.saveProject(address);
                    }
                });
        }
    }

    private openDialog(address) {
        const dialogRef = this.dialog.open(AddressConfirmationComponent, {
            maxWidth: 500,
            panelClass: ["dialog-no-padding", "dialog-no-border"],
        });
        dialogRef.componentInstance.address = address;
        return dialogRef.afterClosed();
    }

    private lookupAddress() {
        const address = this.customerAddress$.value;
        const params: LookupAddressParams = {
            address: address.address,
            address2: address.address2,
            country: address.country,
            zipcode: address.zipcode,
        };

        return this.addressUtilityService.lookupAddress(params).pipe(
            switchMap(addresses => this.openDialog(addresses?.[0])),
            map((selectedAddress: Address) => {
                if (selectedAddress) {
                    const addr = this.processSelectedAddress(selectedAddress);
                    this.customerAddress$.next(addr);
                    return addr;
                }
                return null;
            })
        );
    }

    private processSelectedAddress(selectedAddress: Address) {
        if (selectedAddress) {
            // next the subject with a combo of the existing address key/values
            // overwritten with the selected address key/values
            selectedAddress.id = this.customerAddress$.value.id;

            // Some lookups may return no street results. In those cases, don't override the user input.
            selectedAddress.address = selectedAddress.address || this.customerAddress$.value?.address || "";
            selectedAddress.address2 = selectedAddress.address2 || this.customerAddress$.value?.address2 || "";
            selectedAddress.city = selectedAddress.city || this.customerAddress$.value?.city || "";
            selectedAddress.area = this.customerAddress$.value?.area || "";
            selectedAddress.typeid = this.customerAddress$.value?.typeid;
            selectedAddress.builderid = this.customerAddress$.value?.builderid;
            selectedAddress.communityid = this.customerAddress$.value?.communityid;
            selectedAddress.gateaccesscode = this.customerAddress$.value?.gateaccesscode;
        }

        return selectedAddress;
    }

    private saveProject(selectedAddress?: Address) {
        this.currentProject$
            .pipe(
                filter(project => !!project.id),
                take(1)
            )
            .subscribe(project => {
                this.logProjectCreation(project);
                this.navigateToProject();
            });
        this.isSubmitAttempted = true;
        const entityId = this.companyDetailsStep.getValue();
        const address = selectedAddress || this.customerAddress$.value;
        if (!address?.id) {
            this.projectDispatcher.dispatch(
                new ProjectActions.CreateProjectNoCustomer({
                    project$: this.currentProject$,
                    address,
                    entityId: +entityId,
                })
            );
        } else {
            this.updateAddressInfo(address);
        }
    }

    private updateAddressInfo(address: Address) {
        const addrForm = this.getCloneAddressForm(address);
        const emitter = new EventEmitter();
        emitter
            .pipe(
                tap((action: Action) => {
                    if (action.type === AddressActions.updateSuccess.type) {
                        this.updateProjectAddress(action, address);
                    }
                }),
                take(1)
            )
            .subscribe();
        addrForm.patchValue(address);
        this.addressDispatchers.upsert(addrForm, emitter, null);
    }

    private updateProjectAddress(action, address: Address) {
        this.projectService.dispatch(action);
        this.sendCoordinates(this.existingProject?.claresystem, address);
    }

    private getCloneAddressForm(address: Address) {
        const addrForm = this.formBuilder.group({
            id: [],
            address: [""],
            address2: [],
            city: [""],
            state: [""],
            country: [""],
            zipcode: [""],
            coordinates: [],
            timezonename: [],
            timezoneid: [],
            typeid: [""],
            area: [""],
            builderid: [],
            communityid: [],
            gateaccesscode: [""],
            crossstreet: [""],
            directions: [""],
        });
        addrForm.patchValue(address);
        return addrForm;
    }

    logCustomerCreation(project, customerName: string) {
        const entity = this.companyDetailsStep.getSelectedEntity();
        const logKey = createAuditLogKey(LOG_CATEGORY.COMPANY, LOG_TYPE.CUSTOMER, LOG_SUB_TYPE.ADD);
        this.auditLogService.logEvent(logKey, {
            data: `Customer Name: ${customerName}<br/>Email: ${this.createdCustomer.email}<br/>Company: ${entity.name}`,
            entityId: project.entityId,
            projectId: project.id,
        });
    }

    logProjectCreation(project) {
        const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.ACCOUNT, LOG_SUB_TYPE.CREATE);
        this.auditLogService.logEvent(logKey, {
            data: `Address: ${this.customerAddress$.value.address}`,
            entityId: project.entityId,
            projectId: project.id,
        });
    }

    private logProjectUserAdd(project, customerName: string) {
        const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.ACCOUNT, LOG_SUB_TYPE.CREATE);
        this.auditLogService.logEvent(logKey, {
            data: `Added Primary Customer: ${customerName}`,
            entityId: project.entityId,
            projectId: project.id,
        });
    }

    saveProjectUser(currentStepIndex) {
        const customerInfo = this.primaryCustomerInfoStep.getValue();
        const noCustomer = !customerInfo || customerInfo.noCustomer;
        if (noCustomer) {
            this.completeUpdate.emit();
            return;
        }

        this.updateUserFormField(customerInfo);
        if (!this.existingProject?.id) {
            this.manualTriggerNext(currentStepIndex);
        }

        this.saveNewCustomer(customerInfo);
    }

    saveEntity(stepIndex) {
        if (this.existingProject?.id) {
            this.updateProjectEntity();
        }
        this.manualTriggerNext(stepIndex);
    }

    private updateProjectEntity() {
        const entityId = this.companyDetailsStep.getValue();
        this.onSaveDealer(+entityId, this.initializedData?.project?.entityId);
    }

    private onSaveDealer(entityId: number, oldEntityId?: number) {
        if (!entityId) {
            return;
        }

        this.alulaDevice$.subscribe(alulaDev => this.deactivateServicePlan(alulaDev)).unsubscribe();
        const closeSaveDealerDialog = new EventEmitter();
        // After updating dealer...
        closeSaveDealerDialog
            .pipe(
                take(1),
                tap(_ => {
                    this.existingProject = Object.assign(this.existingProject, { entityId });
                }),
                switchMap(_ =>
                    combineLatest({
                        oldEntity: this.entityService.selectEntity$(oldEntityId).pipe(take(1)),
                        newEntity: this.entityService.selectEntity$(entityId).pipe(take(1)),
                    })
                ),
                tap(({ oldEntity, newEntity }) => {
                    const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.ACCOUNT, LOG_SUB_TYPE.MOVE);
                    const logData = {
                        data: `Original Company: ${oldEntity.name}, Destination Company: ${newEntity.name}`,
                        entityId: this.existingProject.entityId,
                        projectId: this.existingProject.id,
                        optionalParams: { uuid: this.existingProject?.claresystem?.uuid },
                    };
                    this.auditLogService.logEvent(logKey, logData);
                })
            )
            .subscribe();
        const contact = {
            ...(this.initializedData?.user || {}),
            entityId,
        };
        this.projectService.dispatchUpdateProjectDealer(
            { ...this.existingProject, entityId },
            this.existingProject.id,
            closeSaveDealerDialog,
            contact
        );
    }

    private deactivateServicePlan(alulaDeviceCall: ApiResultState<AlulaDevice>) {
        if (alulaDeviceCall?.result?.isMonitoringServiceEnabled()) {
            this.alulaDispatchers.clearAlulaState();
            return this.projectService.deactivateServicePlan(alulaDeviceCall, this.existingProject);
        }
    }

    private saveNewCustomer(customer: Customer) {
        const emitter = {
            emit: (data: { userData: Customer }) => {
                if (data) {
                    this.onboardCustomer(data.userData);
                    this.updateInviteCode(data.userData);
                    const customerName = `${customer.firstName} ${customer.lastName}`;
                    this.logProjectUserAdd(this.existingProject, customerName);
                }

                this.projectDispatcher.clearProjects();
            },
        } as EventEmitter<any>;
        const logData = this.getAuditLogInput(customer);
        this.projectService.upsertCustomer(
            this.customerService.getUpsertAction({ ...customer }, null, logData),
            this.existingProject,
            "userinfo1",
            emitter
        );
    }

    private getAuditLogInput(newCustomer): AuditLogInput {
        const entity = this.companyDetailsStep.getSelectedEntity();
        const newName = `${newCustomer.firstName} ${newCustomer.lastName}`;
        const data = `Customer Name: ${newName}<br/>Email: ${newCustomer.email}<br/>Company: ${entity.name}`;
        const logKey = createAuditLogKey(LOG_CATEGORY.COMPANY, LOG_TYPE.CUSTOMER, newCustomer.id ? LOG_SUB_TYPE.MODIFY : LOG_SUB_TYPE.ADD);

        return createAuditLogInput(logKey, data, {
            id: null,
            entityId: entity.id,
        });
    }

    private updateInviteCode(customer: Customer) {
        this.projectDispatcher.dispatch(
            CustomerActions.updateInviteCode({
                customer: {
                    ...customer,
                    inviteCode: this.customerService.generateCustomerInviteCode(),
                },
            })
        );
    }

    private onboardCustomer(customer: Customer) {
        if (this.existingProject.claresystem) {
            this.assignUserToClareSystem(customer);
        } else {
            this.projectDispatcher.dispatch(
                SnackbarActions.openSnackbar({
                    message: new HttpResponseHandler(null, null, "Please add a controller before inviting your customer"),
                })
            );
        }
    }

    private assignUserToClareSystem(customer: Customer) {
        this.projectDbService
            .connectUserToClaresystem(customer, this.existingProject.claresystem)
            .pipe(
                take(1),
                tap(_ => {
                    this.projectDispatcher.dispatchClearLocationUsers(this.existingProject.claresystem);
                })
            )
            .subscribe();
    }

    public updateControllerToProject(event: any, currentStepIndex: number): void {
        const { clareSystem, securitySystem } = event;
        this.isSubmitAttempted = true;
        const emitter = {
            emit: () => {
                this.sendCoordinates(clareSystem);
                this.manualTriggerNext(currentStepIndex);
                this.projectService.clearActiveProject();
            },
        } as EventEmitter<void>;
        const addr = this.customerAddress$.value;
        const project = this.currentProject$.value;

        this.projectDispatcher.dispatch(
            new ProjectActions.CreateProjectController(
                clareSystem,
                project,
                securitySystem,
                emitter,
                null,
                this.companyDetailsStep.getSelectedEntity(),
                true,
                addr
            )
        );
    }

    private updateUserFormField(customerInfo) {
        this.primaryCustomerInfoStep.form.patchValue(
            {
                phone: this.stripPhoneFormat(customerInfo.phone),
                phone2: this.stripPhoneFormat(customerInfo.phone2),
                phone3: this.stripPhoneFormat(customerInfo.phone3),
            },
            { onlySelf: true, emitEvent: false }
        );
    }

    private sendCoordinates(claresystem: ClareSystem, currentAddress?: Address) {
        if (!claresystem?.uuid) {
            return;
        }
        const project = this.currentProject$.value;
        const getAddress = currentAddress?.id ? of(currentAddress) : this.getAddress(project);
        getAddress
            .pipe(
                take(1),
                tap(address => this.projectService.updateCoordinates(project, claresystem, address))
            )
            .subscribe();
    }

    private getAddress(project: Project): Observable<Address> {
        return this.addressService.getAddressById$(project?.addressId).pipe(
            filter(address => !!address),
            take(1),
            map(address => Object.assign(address, { addressType: "projectAddress" }))
        );
    }

    private navigateToProject(): void {
        if (this.currentProject$.value.id) {
            if (this.isCompletingProcess) {
                this.moveToInProgressStep();
                return;
            }
            this.projectDispatcher.navigate([
                `${accountRoutePaths.root}${accountRoutePaths.edit}/${this.currentProject$.value.id}/summary`,
            ]);
            return;
        }

        this.projectDispatcher.navigate([`${accountRoutePaths.root}`]);
    }

    private manualTriggerNext(index: number): void {
        this.steps[index].isCompleted = true;
        this.cdr.detectChanges();
        this.stepper.next();
    }

    private moveToInProgressStep() {
        let index = this.steps.find(s => s.tag === this._addCustomerTag).id;

        if (!this.uuid) {
            index = this.steps.find(s => s.tag === this._addPanelTag).id;
        }

        let prev = 0;
        while (prev < index) {
            this.steps[prev].isCompleted = true;
            this.manualTriggerNext(prev);
            ++prev;
        }
    }
}
